import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import { FieldworkV2Query } from '../../../../data/gql-gen/questionnaire/graphql'
import {
  QUESTIONNAIRE,
  QuestionnaireData
} from '../../../../data/gql/questionnaire/queries'
import {
  AudienceData,
  GET_AUDIENCES
} from '../../../../data/gql/questionnaire/queries/audiences'
import { GET_FIELDWORK } from '../../../../data/gql/questionnaire/queries/fieldwork'
import { LoggerErrorType } from '../../../../data/logger'
import { useOptionalWaveId } from '../../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../../hooks/useProjectId'
import { useSurveyId } from '../../../../hooks/useSurveyId'
import { flattenEntries } from '../../../../modules/Questionnaire/Questionnaire.utils'
import { languagesInUse } from '../../../../modules/Translations/lib/languagesInUse'
import {
  captureApolloError,
  getLanguageLabel
} from '../../../../utils/HelperFunctions'
import { fieldworkLanguages } from '../../../../utils/fieldworkLanguages'

// English is always the master / source language (the Translations editor
// authors against it regardless of country), so the export overlay treats
// every other language as a translation to apply.
export const MASTER_LANGUAGE = 'en'

export interface ExportLanguageOption {
  id: string
  name: string
  value: string
}

export interface ExportLanguageOptions {
  options: ExportLanguageOption[]
  loading: boolean
  failed: boolean
}

const toOption = (code: string): ExportLanguageOption => ({
  id: code,
  name:
    code === MASTER_LANGUAGE
      ? `${getLanguageLabel(code)} (master)`
      : getLanguageLabel(code),
  value: code
})

export const primarySubtag = (code: string) => code.split('-')[0]

/**
 * A market's export languages: English (master) first, then the languages
 * the market fields in their declared order, reduced to their primary subtag
 * ("de-DE" -> "de", the code translations are stored against) and without
 * English, which the master entry already covers.
 */
export const marketLanguageOptions = (
  fieldwork: Parameters<typeof fieldworkLanguages>[0]
): ExportLanguageOption[] => {
  const codes = new Set(
    fieldworkLanguages(fieldwork)
      .map(primarySubtag)
      .filter((code) => code && code !== MASTER_LANGUAGE)
  )
  return [MASTER_LANGUAGE, ...codes].map(toOption)
}

/**
 * The languages a market's doc can be exported in, from its fieldwork
 * (cache-first, so shared with the other fieldwork reads). Skipped without a
 * survey id.
 */
export const useMarketLanguageOptions = (
  marketSurveyId: string | undefined
): ExportLanguageOptions => {
  const { data, loading, error } = useQuery<FieldworkV2Query>(GET_FIELDWORK, {
    context: { clientName: 'questionnaire' },
    variables: { surveyId: marketSurveyId },
    skip: !marketSurveyId
  })
  const options = useMemo(
    () => marketLanguageOptions(data?.fieldwork),
    [data?.fieldwork]
  )
  return { options, loading, failed: Boolean(error) }
}

/**
 * English (master) first, then every language that has at least one stored
 * translation, alphabetical by display label. Falling back to master text is
 * handled per-field by the export overlay, so only languages actually worth
 * exporting are offered.
 *
 * Reads the shared questionnaire + custom-audience data (cache-first, so this
 * reuses what the editor already loaded) — the same source the Translations
 * sidebar uses. It doesn't depend on the current survey's
 * `languageAllocations`: in multi-market the export runs off the master
 * survey, whose own allocations are empty (they live on the per-market
 * surveys). Skipped while `enabled` is false: the dialogs are mounted on the
 * results pages too, where nothing else needs this data.
 */
export const useTranslatedLanguageOptions = (
  enabled: boolean
): ExportLanguageOptions => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  const {
    data: questionnaireData,
    loading: questionnaireLoading,
    error: questionnaireError
  } = useQuery<QuestionnaireData>(QUESTIONNAIRE, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, surveyId, waveId },
    skip: !enabled,
    onError: (error) =>
      captureApolloError(
        LoggerErrorType.ApolloQuery,
        'draftQuestionnaireV2',
        error
      )
  })
  const {
    data: audienceData,
    loading: audienceLoading,
    error: audienceError
  } = useQuery<AudienceData>(GET_AUDIENCES, {
    context: { clientName: 'questionnaire' },
    variables: { projectId, surveyId, waveId },
    skip: !enabled,
    onError: (error) =>
      captureApolloError(LoggerErrorType.ApolloQuery, 'draftAudienceV2', error)
  })

  const options = useMemo(() => {
    const entries = flattenEntries(
      questionnaireData?.draftQuestionnaire.entries ?? []
    )
    const audienceMembers = audienceData?.draftAudience.members ?? []
    const translated = Array.from(languagesInUse(entries, audienceMembers))
      .filter((code) => code !== MASTER_LANGUAGE)
      .sort((a, b) => getLanguageLabel(a).localeCompare(getLanguageLabel(b)))

    return [MASTER_LANGUAGE, ...translated].map(toOption)
  }, [
    questionnaireData?.draftQuestionnaire.entries,
    audienceData?.draftAudience.members
  ])

  return {
    options,
    loading: questionnaireLoading || audienceLoading,
    // On failure the list collapses to English alone, so exporting would ship
    // master text as if it were the requested language. Callers block it.
    failed: Boolean(questionnaireError || audienceError)
  }
}

/**
 * The option to preselect for a caller's `preferred` language (a fieldwork
 * code, which need not have translations): an exact match, else one on the
 * primary subtag ("de-DE" -> "de"), else English (master).
 */
export const pickLanguageOption = (
  options: ExportLanguageOption[],
  preferred: string
): string =>
  (
    options.find((option) => option.value === preferred) ??
    options.find(
      (option) => primarySubtag(option.value) === primarySubtag(preferred)
    )
  )?.value ?? MASTER_LANGUAGE
